import axios from 'axios'
import { getSessionCookies } from 'portal-utils/session-cookies'
import { JWT_STORAGE_KEY } from 'portal-modules/common/constants/keys'
import { getBaseUrl } from '../constants/api-config'

// Single module-level axios instance. The request interceptor resolves
// `baseURL` and the current JWT at call time, so this module does not
// capture stale values at import time (important in tests and for
// post-login JWT rotation).
export const client = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'x-app-origin': 'tpweb',
  },
})

client.interceptors.request.use(config => {
  config.baseURL = getBaseUrl()
  const token = getSessionCookies(JWT_STORAGE_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  } else {
    // Defensive: ensure a stale Authorization from a prior authenticated
    // call does not leak into a call made after logout.
    delete config.headers.Authorization
  }
  return config
})

// NOTE (intentional): no response interceptor for 401/403.
//
// As of this writing, no axios client in tenantportalui (this module
// nor src/api/api.js, common/modules/common/api/commonApi.js, etc.)
// installs a response interceptor for auth errors, and no Redux module
// checks for 401/403 anywhere in the codebase. There is no shared
// session-expiry policy to mirror — adding a one-off interceptor here
// would invent a new policy ("what does this feature do on 401?")
// that other features do not follow, creating exactly the per-feature
// drift a centralized interceptor is supposed to prevent. Worse, it
// would do so silently: a logout/redirect from this module's HTTP
// would surprise users who can still see a stale UI elsewhere on
// the page.
//
// The right fix is at the shared layer (src/api/api.js's createMethod
// or a new common axios wrapper): a single interceptor that all
// modules opt into, with a documented session-expiry UX (e.g.
// dispatch logout, soft-redirect to /login, or attempt refresh) and
// a rollout plan that touches every Redux module simultaneously.
// That work is intentionally out of scope for this PR. Leaving this
// note so the next maintainer (or the next code reviewer to ask the
// same question) can find the rationale here.

export const getProjects = suiteId =>
  client.get(`/suites/${encodeURIComponent(suiteId)}/projects`)

export const getProjectBudgetSummary = id =>
  client.get(`/projects/${id}/budget-summary-financial`)

export const getBudgetItemInvoices = budgetItemId =>
  client.get(`/budget-items/${budgetItemId}/invoices/financial`)

// Per-project document list. Mirrors the route used by the reference
// tenant-improvement-ui repo (DocumentApiService.getProjectDocuments).
// Returns `{ documents: [...], pagination: {...} }`.
export const getDocuments = projectId =>
  client.get(`/documents/projects/${encodeURIComponent(projectId)}`)

// View / Download both stream the file body. The backend protects these
// routes with the same JWT used for `/documents/*` listing, so a plain
// <a href> won't carry credentials — we have to hit the endpoint with the
// authenticated axios client and get a Blob back. Mirrors
// DocumentApiService.viewDocument / downloadDocument in the reference repo.
export const getDocumentViewBlob = documentId =>
  client.get(`/documents/${encodeURIComponent(documentId)}/view`, {
    responseType: 'blob',
  })

export const getDocumentDownloadBlob = documentId =>
  client.get(`/documents/${encodeURIComponent(documentId)}/download`, {
    responseType: 'blob',
  })
